import React, { useState } from 'react'

import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'

import { Button, Card, Input } from '@repo/ui'

/**
 * Settings section-style comparison — the four candidate treatments, side by side on
 * identical content, so a choice about them can be looked at rather than described.
 *
 * It exists because the first attempt at this comparison was a sequence of screenshots
 * taken by applying each variant and reverting it. That is not a comparison anyone else
 * can check: the app ends up back at the baseline and the alternatives exist only in a
 * transcript. Rendering all four here makes the decision inspectable and re-checkable.
 *
 * A TOGGLE rather than four columns on purpose. Three of these variants differ only in
 * surface luminance and border, and small deltas are far easier to judge by flipping
 * between two states at the same size and position than by comparing two smaller copies
 * side by side. The measured contrast figures are printed under the toggle because the
 * eye is unreliable exactly where these variants differ — a step that reads fine on one
 * display can vanish on another.
 *
 * Each variant is implemented LOCALLY here, not by editing the shared `SettingsSection`.
 * The lab is for deciding; only the winner should touch the component 15 settings pages
 * consume.
 */

type VariantKey = 'a' | 'b' | 'ca' | 'cb' | 'e' | 'f'

interface Variant {
  key: VariantKey
  label: string
  summary: string
  /** Measured, not estimated — see the note in the panel. */
  measurements: string
}

const VARIANTS: Variant[] = [
  {
    key: 'a',
    label: 'A — current',
    summary:
      'Reference. Micro-label above the section at 12px, and a 1px border on contained bodies.',
    measurements: 'border carries the separation; label 12px/600/secondary',
  },
  {
    key: 'b',
    label: 'B — title inside (bordered)',
    summary: 'Reference. Title inside the container at 16px, with the border still on.',
    measurements: 'border carries the separation; title 16px/600',
  },
  {
    key: 'ca',
    label: 'C+A — no border, label above (bigger)',
    summary:
      'Borderless. The label stays ABOVE the section but is bigger and brighter: 13px, 700, primary rather than secondary. Space separates the sections; the label signposts them.',
    measurements: 'card vs page 1.277:1 — the surface is faint, so the GAP is doing the separating',
  },
  {
    key: 'e',
    label: 'E — no containers at all',
    summary:
      'The end-point of "stop using cards as separators" + "fewer borders": there is no container. Every section is a bigger label and its content, separated by space. Nothing to see-through, nothing to mis-read as arbitrary.',
    measurements:
      'no card, so no card-vs-page contrast to fail — 32px between sections is the whole separation',
  },
  {
    key: 'f',
    label: 'F — all contained, title inside',
    summary:
      'The other coherent end-point: EVERY section is contained, borderless, title inside at 18px. Nothing is half-in — so the question "why is this one in a panel?" cannot arise. The container stops meaning "these belong together" and becomes a panel of content, which is an honest role once no section is without one.',
    measurements:
      'card vs page 1.277:1 on every section — uniform, so the faintness no longer has to carry a distinction',
  },
  {
    key: 'cb',
    label: 'C+B — no border, title inside (bigger)',
    summary:
      'Borderless with the title INSIDE at 18px/600, a fuller description, and 24px from the head down to the first field. With no border, having the head inside is what tells you the card is there at all.',
    measurements: 'card vs page 1.277:1 — the head inside reinforces a surface the border no longer marks',
  },
]

/** The head + body of one section, rendered per variant. */
const Section = ({
  variant,
  title,
  description,
  action,
  contained,
  children,
}: {
  variant: VariantKey
  title: string
  description?: string
  action?: React.ReactNode
  contained: boolean
  children: React.ReactNode
}) => {
  const theme = useTheme()
  const body = <Stack spacing={4}>{children}</Stack>

  // B and C+B: the head lives inside the container, sentence case.
  if (variant === 'b' || variant === 'cb' || variant === 'f') {
    const big = variant === 'cb' || variant === 'f'
    const head = (
      // C+B gets 24px from the head to the first field, against B's 16px: with no border
      // the head and the fields share one uninterrupted surface, so the gap is the only
      // thing separating the section's name from its contents.
      <Stack spacing={big ? 2 : 1} sx={{ mb: big ? 6 : 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant={big ? 'body1' : 'body2'}
            sx={{ fontWeight: 600, fontSize: big ? 18 : undefined }}
          >
            {title}
          </Typography>
          {action}
        </Stack>
        {description && (
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {description}
          </Typography>
        )}
      </Stack>
    )
    // F ignores `contained` the way E does, but in the opposite direction.
    const showCard = variant === 'f' ? true : contained
    return (
      <Stack>
        {showCard ? (
          <Card sx={{ m: 0, padding: 5, ...(big ? { border: 'none' } : {}) }}>
            {head}
            {body}
          </Card>
        ) : (
          <>
            {head}
            {body}
          </>
        )}
      </Stack>
    )
  }

  // A / C+A / E: the head sits above the content.
  // E ignores `contained` entirely — that is the point of it.
  const borderless = variant === 'ca' || variant === 'e'
  const showCard = contained && variant !== 'e'
  const cardSx = borderless ? { m: 0, border: 'none', padding: 5 } : { m: 0 }

  return (
    <Stack>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant="body4"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              // C+A: 13px/700/primary, against A's 12px/600/secondary. At 12px secondary
              // the label was quiet enough that, with the border also gone, nothing was
              // marking where a section started.
              fontSize: borderless ? 13 : undefined,
              fontWeight: borderless ? 700 : 600,
              color: borderless ? theme.palette.text.primary : theme.palette.text.secondary,
            }}
          >
            {title}
          </Typography>
          {action}
        </Stack>
        {description && (
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {description}
          </Typography>
        )}
      </Stack>
      {showCard ? <Card sx={cardSx}>{body}</Card> : body}
    </Stack>
  )
}

/** A two-field row, the shape most settings cards actually hold. */
const FieldRow = () => (
  <Stack direction="row" spacing={4}>
    <Box sx={{ flex: 1 }}>
      <Input label="Name" value="Louis Vuitton" onValueChange={() => {}} fullWidth />
    </Box>
    <Box sx={{ flex: 1 }}>
      <Input label="Country" value="United States" onValueChange={() => {}} fullWidth />
    </Box>
  </Stack>
)

const SaveRow = () => (
  <Stack direction="row" spacing={3} justifyContent="flex-end">
    <Button variant="secondary" disabled>
      Cancel
    </Button>
    <Button disabled>Save</Button>
  </Stack>
)

export const SectionStyleLab = () => {
  const theme = useTheme()
  const [variant, setVariant] = useState<VariantKey>('a')
  const active = VARIANTS.find(v => v.key === variant) ?? VARIANTS[0]

  return (
    <Stack spacing={6}>
      <Stack spacing={2}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Settings section style — four candidates
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Flip between them on identical content. The second block is the case that decides
          it: a page that MIXES a contained section with two uncontained ones, which is what
          Profile actually looks like.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
        {VARIANTS.map(v => (
          <Button
            key={v.key}
            variant={v.key === variant ? 'primary' : 'secondary'}
            onClick={() => setVariant(v.key)}
          >
            {v.label}
          </Button>
        ))}
      </Stack>

      <Card sx={{ m: 0 }}>
        <Stack spacing={1}>
          <Typography variant="body3" sx={{ fontWeight: 600 }}>
            {active.label}
          </Typography>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {active.summary}
          </Typography>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Measured in the browser: {active.measurements}
          </Typography>
        </Stack>
      </Card>

      <Divider />

      {/* Case 1 — every section contained. This is where B looks best. */}
      <Stack spacing={2}>
        <Typography variant="body4" color="text.secondary">
          CASE 1 — all sections contained (what Company → General looks like)
        </Typography>
        <Stack spacing={8}>
          <Section variant={variant} title="Company" contained>
            <FieldRow />
            <SaveRow />
          </Section>
          <Section
            variant={variant}
            title="Support"
            description="Provide your brand's support contact details to help certificate owners reach your support team."
            contained
          >
            <FieldRow />
            <SaveRow />
          </Section>
        </Stack>
      </Stack>

      <Divider />

      {/* Case 2 — mixed. Under B the boxed and unboxed heads are identical, so the box
          stops meaning "these belong together" and reads as arbitrary. */}
      <Stack spacing={2}>
        <Typography variant="body4" color="text.secondary">
          CASE 2 — contained AND uncontained on one page (what Profile looks like)
        </Typography>
        <Stack spacing={8}>
          <Section variant={variant} title="Login" contained>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack spacing={1}>
                <Typography variant="body3">Email</Typography>
                <Typography variant="body3" color="text.secondary">
                  sjfstebbins@gmail.com
                </Typography>
              </Stack>
              <Button variant="secondary">Change</Button>
            </Stack>
          </Section>

          <Section
            variant={variant}
            title="Preferences"
            description="Remember your current filters, sort and table view."
            contained={false}
          >
            <Typography variant="body3" color="text.secondary">
              Save state on next login
            </Typography>
          </Section>

          <Section variant={variant} title="Delete account" contained={false}>
            <Typography variant="body3" color="text.secondary">
              Deleted accounts cannot be restored.
            </Typography>
            <Box>
              <Button variant="secondary" destructive="hover">
                Delete
              </Button>
            </Box>
          </Section>
        </Stack>
      </Stack>
    </Stack>
  )
}
